import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBarBody,
  MessageBarTitle,
  Textarea,
} from '@fluentui/react-components';
import { Dismiss16Regular, Warning16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideField } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideTable, GuideTh } from '../../../theme/components/GuideTable';
import { StatusPill } from '../../../theme/components/StatusPill';
import { StatusTone } from '../../../theme/guideTokens';
import { getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';
import {
  addMark,
  buildParts,
  MARK_LABELS,
  MarkLabel,
  markAt,
  marksFromParts,
  matchSummary,
  rangeOf,
  removeMarkAt,
  sameParts,
  TemplateMark,
  templateTestTexts,
  tokenize,
  validateParts,
} from '../../../functions/ProtocolReferenceTemplate.Functions';
import {
  ProtocolReferenceTemplate,
  REFERENCE_TEMPLATE_NAME_MAX,
  REFERENCE_TEMPLATE_SAMPLE_MAX,
  ReferenceTemplatePart,
  SaveReferenceTemplateRequest,
  TemplateTestResult,
} from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { useDebouncedTemplateTest } from './useDebouncedTemplateTest';
import './ProviderConsumptionImport.Styles.css';

/** Atajo de teclado de cada marca (además de los botones). */
export const MARK_SHORTCUTS: Record<MarkLabel, string> = { protocol: 'p', patient: 'a', center: 'c', service: 's', any: 'i' };

/** Símbolo de cada marca: acompaña al rótulo (nunca sólo color). */
const MARK_SYMBOLS: Record<MarkLabel, string> = { protocol: '#', patient: '@', center: '⌂', service: '§', any: '…' };

export interface ProtocolReferenceTemplateEditorProps {
  open: boolean;
  onDismiss: () => void;
  /** Plantilla a editar (PUT); sin ella, alta (POST). */
  template?: ProtocolReferenceTemplate;
  /** Texto de ejemplo del alta ("Definir formato a partir de este ítem"). */
  sampleText?: string | null;
  /** Contexto debajo del título (p. ej. "Fila 1"). */
  context?: string;
  /** Otros textos para la prueba en vivo (los nombres de los ítems de la página); el de ejemplo va siempre primero. */
  testTexts?: Array<string | null | undefined>;
  onTest: (parts: ReferenceTemplatePart[], texts: string[]) => Promise<TemplateTestResult[]>;
  /** Guarda: `templateId` presente = edición. Rechaza con ErrorType (400 validación, 409 ya hay 20 activas). */
  onSave: (body: SaveReferenceTemplateRequest, templateId?: string) => Promise<ProtocolReferenceTemplate | undefined>;
  /** Se guardó (el llamador cierra, recarga y avisa). */
  onSaved?: (template: ProtocolReferenceTemplate | undefined, name: string) => void;
}

const fieldTone: Record<MarkLabel, StatusTone> = { protocol: 'brand', patient: 'info', center: 'confirmed', service: 'success', any: 'neutral' };

/**
 * Editor de formatos de referencia (spec 009, US5, FR-017): el texto de ejemplo partido en palabras y separadores; se
 * elige un tramo con el mouse (clic, arrastre o Mayús + clic) o con el teclado (flechas, Mayús + flechas, Inicio / Fin)
 * y se marca como Protocolo / Paciente / Centro / Servicio / Ignorar con los botones o con P, A, C, S, I (Supr quita).
 * Lo no marcado queda como texto fijo. Valida con las reglas del servidor y prueba en vivo (300 ms) con los textos de la
 * página. Los textos del archivo se muestran como texto (nunca HTML).
 */
export const ProtocolReferenceTemplateEditor = (props: ProtocolReferenceTemplateEditorProps) => {
  const { open, onDismiss, template, context, onTest, onSave, onSaved } = props;
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.templates.editor.${key}`, options) as string;
  const trf = (label: MarkLabel) => t(`providerConsumptionImport.templates.fields.${label}`) as string;

  const [name, setName] = useState('');
  const [sample, setSample] = useState('');
  const [marks, setMarks] = useState<TemplateMark[]>([]);
  const [anchor, setAnchor] = useState<number | undefined>(undefined);
  const [focus, setFocus] = useState<number | undefined>(undefined);
  const [restoreWarning, setRestoreWarning] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(undefined);
  const cells = useRef<Array<HTMLSpanElement | null>>([]);
  const dragging = useRef(false);

  // Al abrir: el formato a editar (marcas recuperadas sobre su ejemplo) o el texto del ítem.
  useEffect(() => {
    if (!open) return;
    const text = (template ? template.sampleText : props.sampleText) ?? '';
    const initialMarks = template ? marksFromParts(text, template.parts) : [];
    setName(template?.name ?? tr('defaultName'));
    setSample(text);
    setMarks(initialMarks);
    setRestoreWarning(!!template && !sameParts(buildParts(tokenize(text), initialMarks), template.parts));
    setAnchor(undefined);
    setFocus(undefined);
    setSubmitted(false);
    setSaving(false);
    setSaveError(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template]);

  useEffect(() => {
    const stop = () => {
      dragging.current = false;
    };
    window.addEventListener('mouseup', stop);
    return () => window.removeEventListener('mouseup', stop);
  }, []);

  const tokens = useMemo(() => tokenize(sample), [sample]);
  const parts = useMemo(() => buildParts(tokens, marks), [tokens, marks]);
  const errors = useMemo(() => validateParts(parts), [parts]);
  const othersKey = JSON.stringify(props.testTexts ?? []);
  const texts = useMemo(() => templateTestTexts(sample, props.testTexts ?? []), [sample, othersKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const test = useDebouncedTemplateTest(parts, texts, onTest, open && errors.length === 0);

  const selection = anchor !== undefined && focus !== undefined ? rangeOf(anchor, focus) : undefined;
  const selectionText = selection ? tokens.slice(selection.from, selection.to + 1).map((token) => token.text).join('') : '';
  const trimmedName = name.trim();
  const nameError = !trimmedName ? tr('nameRequired') : trimmedName.length > REFERENCE_TEMPLATE_NAME_MAX ? tr('nameTooLong', { max: REFERENCE_TEMPLATE_NAME_MAX }) : undefined;
  const sampleTooLong = sample.trim().length > REFERENCE_TEMPLATE_SAMPLE_MAX;
  const canSave = !saving && errors.length === 0 && !nameError && !sampleTooLong && tokens.length > 0;

  const moveTo = (index: number, extend: boolean) => {
    if (tokens.length === 0) return;
    const next = Math.max(0, Math.min(tokens.length - 1, index));
    setFocus(next);
    if (!extend || anchor === undefined) setAnchor(next);
    cells.current[next]?.focus();
  };

  const applyMark = (label: MarkLabel) => {
    if (!selection) return;
    setMarks((current) => addMark(current, { ...selection, label }));
  };

  const removeSelectionMarks = () => {
    if (!selection) return;
    setMarks((current) => {
      let next = current;
      for (let index = selection.from; index <= selection.to; index++) next = removeMarkAt(next, index);
      return next;
    });
  };

  const onCellKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>, index: number) => {
    const extend = event.shiftKey;
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        moveTo((focus ?? index) + 1, extend);
        return;
      case 'ArrowLeft':
        event.preventDefault();
        moveTo((focus ?? index) - 1, extend);
        return;
      case 'Home':
        event.preventDefault();
        moveTo(0, extend);
        return;
      case 'End':
        event.preventDefault();
        moveTo(tokens.length - 1, extend);
        return;
      case 'Delete':
      case 'Backspace':
        event.preventDefault();
        removeSelectionMarks();
        return;
      default:
        break;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const label = MARK_LABELS.find((entry) => MARK_SHORTCUTS[entry] === event.key.toLowerCase());
    if (label) {
      event.preventDefault();
      applyMark(label);
    }
  };

  const onCellMouseDown = (event: React.MouseEvent<HTMLSpanElement>, index: number) => {
    event.preventDefault();
    dragging.current = true;
    setFocus(index);
    if (!event.shiftKey || anchor === undefined) setAnchor(index);
    cells.current[index]?.focus();
  };

  const onCellMouseEnter = (index: number) => {
    if (dragging.current) setFocus(index);
  };

  const changeSample = (value: string) => {
    setSample(value);
    setMarks([]);
    setAnchor(undefined);
    setFocus(undefined);
    setRestoreWarning(false);
  };

  const save = async () => {
    setSubmitted(true);
    if (!canSave) return;
    setSaving(true);
    setSaveError(undefined);
    try {
      const body: SaveReferenceTemplateRequest = { name: trimmedName, parts, sampleText: sample.trim() || null };
      if (template) body.active = template.active;
      const saved = await onSave(body, template?.id);
      setSaving(false);
      onSaved?.(saved, trimmedName);
    } catch (error: unknown) {
      setSaveError(error);
      setSaving(false);
    }
  };

  const tokenLabel = (text: string) => (text.trim() ? text : tr('space'));

  const renderTokens = () => {
    if (tokens.length === 0) return <p className="pci-secondary-cell">{tr('noTokens')}</p>;
    const focusIndex = focus ?? 0;
    return (
      <div
        role="grid"
        aria-label={tr('tokensLabel')}
        aria-multiselectable="true"
        aria-describedby="pci-template-help"
        className="pci-template-tokens"
        data-testid="pci-template-tokens"
      >
        <div role="row" className="pci-template-tokens__row">
          {tokens.map((token) => {
            const mark = markAt(marks, token.index);
            const selected = !!selection && selection.from <= token.index && token.index <= selection.to;
            const first = !!mark && mark.from === token.index;
            const classes = ['pci-token', `pci-token--${token.kind}`];
            if (mark) classes.push('pci-token--marked', `pci-token--${mark.label}`);
            if (first) classes.push('pci-token--first');
            if (mark && mark.to === token.index) classes.push('pci-token--last');
            if (selected) classes.push('pci-token--selected');
            return (
              <span
                key={token.index}
                ref={(element) => {
                  cells.current[token.index] = element;
                }}
                role="gridcell"
                tabIndex={token.index === focusIndex ? 0 : -1}
                aria-selected={selected}
                aria-label={mark ? tr('tokenMarked', { text: tokenLabel(token.text), field: trf(mark.label) }) : tokenLabel(token.text)}
                className={classes.join(' ')}
                onKeyDown={(event) => onCellKeyDown(event, token.index)}
                onMouseDown={(event) => onCellMouseDown(event, token.index)}
                onMouseEnter={() => onCellMouseEnter(token.index)}
                onFocus={() => {
                  if (focus === undefined) {
                    setFocus(token.index);
                    setAnchor(token.index);
                  }
                }}
                data-testid={`pci-token-${token.index}`}
                data-mark={mark?.label}
              >
                {first && mark && (
                  <span className="pci-token__label" aria-hidden="true">
                    {MARK_SYMBOLS[mark.label]} {trf(mark.label)}
                  </span>
                )}
                <span className="pci-token__text">{token.text}</span>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const markText = (mark: TemplateMark) => tokens.slice(mark.from, mark.to + 1).map((token) => token.text).join('');

  return (
    <Dialog open={open} modalType="modal" onOpenChange={(_event, data) => !data.open && !saving && onDismiss()}>
      <DialogSurface className="pci-template-editor" data-testid="pci-template-editor">
        <DialogBody>
          <DialogTitle>{template ? tr('titleEdit') : tr('titleNew')}</DialogTitle>
          <DialogContent>
            <div className="pci-stack">
              {context && <span className="pci-secondary">{context}</span>}
              <p className="pci-secondary-cell">{tr('intro')}</p>
              <GuideField label={tr('name')} required hint={tr('nameHint')} counter={{ value: name.length, max: REFERENCE_TEMPLATE_NAME_MAX }} error={submitted ? nameError : undefined}>
                <Input value={name} maxLength={REFERENCE_TEMPLATE_NAME_MAX} onChange={(_event, data) => setName(data.value)} data-testid="pci-template-name" />
              </GuideField>
              <GuideField
                label={tr('sample')}
                hint={tr('sampleHint')}
                counter={{ value: sample.length, max: REFERENCE_TEMPLATE_SAMPLE_MAX }}
                error={sampleTooLong ? tr('sampleTooLong', { max: REFERENCE_TEMPLATE_SAMPLE_MAX }) : undefined}
              >
                <Textarea value={sample} resize="vertical" onChange={(_event, data) => changeSample(data.value)} data-testid="pci-template-sample" />
              </GuideField>

              <div className="pci-template-marks" role="toolbar" aria-label={tr('toolbarLabel')} data-testid="pci-template-toolbar">
                {MARK_LABELS.map((label) => (
                  <Button
                    key={label}
                    size="small"
                    appearance="secondary"
                    className={`pci-template-mark-button pci-template-mark-button--${label}`}
                    disabled={!selection}
                    aria-keyshortcuts={MARK_SHORTCUTS[label].toUpperCase()}
                    aria-label={tr('markAs', { field: trf(label) })}
                    onClick={() => applyMark(label)}
                    data-testid={`pci-template-mark-${label}`}
                  >
                    <span aria-hidden="true">{MARK_SYMBOLS[label]}</span> {trf(label)}
                  </Button>
                ))}
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<Dismiss16Regular />}
                  disabled={!selection || !marks.some((mark) => selection && !(mark.to < selection.from || mark.from > selection.to))}
                  aria-keyshortcuts="Delete"
                  onClick={removeSelectionMarks}
                  data-testid="pci-template-unmark"
                >
                  {tr('removeMark')}
                </Button>
              </div>
              <p id="pci-template-help" className="pci-secondary">
                {tr('tokensHelp')}
              </p>
              {renderTokens()}
              <div className="pci-secondary" aria-live="polite" data-testid="pci-template-selection">
                {selection ? tr('selection', { text: selectionText }) : tr('noSelection')}
              </div>

              <div className="pci-template-mark-list" data-testid="pci-template-mark-list">
                <span className="pci-template-mark-list__title">{tr('marksTitle')}</span>
                {marks.length === 0 ? (
                  <span className="pci-secondary-cell">{tr('noMarks')}</span>
                ) : (
                  <ul className="pci-list pci-list--compact">
                    {marks.map((mark) => (
                      <li key={`${mark.from}-${mark.label}`} data-testid={`pci-template-mark-item-${mark.label}`}>
                        <StatusPill tone={fieldTone[mark.label]} symbol={MARK_SYMBOLS[mark.label]} className="pci-review-pill">
                          {trf(mark.label)}
                        </StatusPill>{' '}
                        <span className="pci-review-name">{markText(mark)}</span>{' '}
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<Dismiss16Regular />}
                          aria-label={tr('removeMarkOf', { field: trf(mark.label) })}
                          onClick={() => setMarks((current) => removeMarkAt(current, mark.from))}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {restoreWarning && (
                <GuideMessageBar intent="warning" layout="multiline" data-testid="pci-template-restore-warning">
                  <MessageBarBody>{tr('restoreWarning')}</MessageBarBody>
                </GuideMessageBar>
              )}
              {tokens.length > 0 && errors.length > 0 && (
                <GuideMessageBar intent="warning" layout="multiline" data-testid="pci-template-errors">
                  <MessageBarBody>
                    <MessageBarTitle>{tr('invalidTitle')}</MessageBarTitle>
                    <ul className="pci-list pci-list--compact">
                      {errors.map((error) => (
                        <li key={error} data-testid={`pci-template-error-${error}`}>
                          {tr(`errors.${error}`)}
                        </li>
                      ))}
                    </ul>
                  </MessageBarBody>
                </GuideMessageBar>
              )}

              <TemplateTestPanel results={test.results} loading={test.loading} error={test.error} waiting={errors.length > 0 || tokens.length === 0} />

              {saveError !== undefined && (
                <GuideMessageBar intent="error" layout="multiline" data-testid="pci-template-save-error">
                  <MessageBarBody>
                    <MessageBarTitle>{tr('saveError')}</MessageBarTitle>
                    {getApiErrorMessage(saveError)}
                  </MessageBarBody>
                </GuideMessageBar>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onDismiss} disabled={saving} data-testid="pci-template-cancel">
              {tr('cancel')}
            </Button>
            <Button appearance="primary" onClick={save} disabled={!canSave} aria-busy={saving || undefined} data-testid="pci-template-save">
              {saving ? tr('saving') : tr('save')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export interface TemplateTestPanelProps {
  results?: TemplateTestResult[];
  loading: boolean;
  error?: unknown;
  /** Todavía no se puede probar (partes inválidas o sin texto). */
  waiting?: boolean;
}

/** "Coincide en N de M" + tabla texto → protocolo / paciente / centro / servicio (con la correspondencia recordada). */
export const TemplateTestPanel = ({ results, loading, error, waiting }: TemplateTestPanelProps) => {
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.templates.test.${key}`, options) as string;
  const summary = results ? matchSummary(results) : undefined;

  const protocolCell = (result: TemplateTestResult) => {
    if (!result.matched) return <span className="pci-secondary-cell">—</span>;
    const extracted = result.extracted?.protocol?.trim();
    if (result.status === 'Matched') return <span>{result.protocolName?.trim() || extracted || '—'}</span>;
    return (
      <span className="pci-review-suggestion pci-review-suggestion--warning">
        <Warning16Regular aria-hidden />
        <span>
          {extracted || '—'} · {tr(`status.${result.status}`)}
        </span>
      </span>
    );
  };

  const patientCell = (result: TemplateTestResult) => {
    const patient = result.patientNumberInProtocol?.trim() || result.extracted?.patient?.trim();
    if (!patient) return <span className="pci-secondary-cell">—</span>;
    if (result.patientValid === true)
      return (
        <span>
          {patient}{' '}
          <StatusPill tone="success" className="pci-review-pill">
            {tr('patientValid')}
          </StatusPill>
        </span>
      );
    if (result.patientValid === false)
      return (
        <span>
          {patient}{' '}
          <StatusPill tone="warning" symbol="!" className="pci-review-pill">
            {tr('patientInvalid')}
          </StatusPill>
        </span>
      );
    return <span>{patient}</span>;
  };

  const serviceCell = (result: TemplateTestResult, index: number) => {
    const key = result.extracted?.service?.trim();
    if (!key) return <span className="pci-secondary-cell">—</span>;
    const mapped = result.serviceMapping;
    return (
      <span data-testid={`pci-template-test-service-${index}`}>
        {mapped ? tr('serviceMapped', { key, service: mapped.name?.trim() || `#${mapped.medicalServiceId}` }) : key}
      </span>
    );
  };

  return (
    <section className="pci-template-test" aria-labelledby="pci-template-test-title" data-testid="pci-template-test">
      <div className="pci-template-test__header">
        <span id="pci-template-test-title" className="pci-template-mark-list__title">
          {tr('title')}
        </span>
        <span aria-live="polite" data-testid="pci-template-test-summary">
          {loading ? tr('testing') : waiting ? tr('waiting') : summary ? tr('summary', { matched: summary.matched, total: summary.total }) : ''}
        </span>
      </div>
      {error !== undefined && !loading && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pci-template-test-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('error')}</MessageBarTitle>
            {getApiErrorMessage(error)}
          </MessageBarBody>
        </GuideMessageBar>
      )}
      {results && results.length > 0 && !waiting && (
        <GuideTable ariaLabel={tr('tableLabel')} data-testid="pci-template-test-table">
          <thead>
            <tr>
              <GuideTh>{tr('columns.text')}</GuideTh>
              <GuideTh>{tr('columns.result')}</GuideTh>
              <GuideTh>{tr('columns.protocol')}</GuideTh>
              <GuideTh>{tr('columns.patient')}</GuideTh>
              <GuideTh>{tr('columns.center')}</GuideTh>
              <GuideTh>{tr('columns.service')}</GuideTh>
            </tr>
          </thead>
          <tbody aria-busy={loading || undefined}>
            {results.map((result, index) => (
              <tr key={index} data-testid={`pci-template-test-row-${index}`}>
                <td>
                  <span className="pci-review-name">{result.text}</span>
                </td>
                <td>
                  {result.matched ? (
                    <StatusPill tone="success" symbol="✓" className="pci-review-pill">
                      {tr('matched')}
                    </StatusPill>
                  ) : (
                    <StatusPill tone="neutral" symbol="×" className="pci-review-pill">
                      {tr('notMatched')}
                    </StatusPill>
                  )}
                </td>
                <td>{protocolCell(result)}</td>
                <td>{patientCell(result)}</td>
                <td>{result.extracted?.center?.trim() || <span className="pci-secondary-cell">—</span>}</td>
                <td>{serviceCell(result, index)}</td>
              </tr>
            ))}
          </tbody>
        </GuideTable>
      )}
    </section>
  );
};

export default ProtocolReferenceTemplateEditor;
